import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import { Link } from 'react-router-dom'
import Breadcrumbs from '../components/Breadcrumbs'

type Contract = {
  id: number
  title: string
  start_date: string | null
  end_date: string | null
  status: string
  notes: string | null
}

type Ticket = {
  id: number
  subject: string
  status: string
  created_at: string
  updated_at: string
}

export default function Portal() {
  const { t } = useTranslation()
  const [contracts, setContracts] = useState<Contract[]>([])
  const [tickets, setTickets] = useState<Ticket[]>([])
  const [loading, setLoading] = useState(true)

  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [creating, setCreating] = useState(false)

  const refresh = async () => {
    const [c, tk] = await Promise.all([
      axios.get('/api/portal/contracts'),
      axios.get('/api/portal/tickets'),
    ])
    setContracts(c.data?.contracts ?? [])
    setTickets(tk.data?.tickets ?? [])
  }

  useEffect(() => {
    let mounted = true
    ;(async () => {
      try {
        await refresh()
      } finally {
        if (mounted) setLoading(false)
      }
    })()
    return () => {
      mounted = false
    }
  }, [])

  const createTicket = async (e: React.FormEvent) => {
    e.preventDefault()
    setCreating(true)
    try {
      await axios.post('/api/portal/tickets', { subject, message })
      setSubject('')
      setMessage('')
      await refresh()
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="min-h-screen pb-24">
      <Breadcrumbs items={[{ key: 'portal' }]} />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold">{t('portal.title')}</h1>
          </div>
          <Link
            to="/support"
            className="inline-flex items-center justify-center px-5 py-3 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 font-semibold hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          >
            {t('nav.support')}
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-slate-200 dark:bg-slate-700 animate-pulse rounded-2xl h-64" />
            <div className="bg-slate-200 dark:bg-slate-700 animate-pulse rounded-2xl h-64" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Contracts */}
            <section className="bg-white dark:bg-slate-800 rounded-2xl p-6 border border-slate-200 dark:border-slate-700 shadow-sm">
              <h2 className="text-xl font-bold mb-4">{t('portal.contracts')}</h2>
              {contracts.length === 0 ? (
                <p className="text-slate-600 dark:text-slate-400 text-sm">{t('portal.noContracts')}</p>
              ) : (
                <ul className="space-y-3">
                  {contracts.map((c) => (
                    <li key={c.id} className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900/40 border border-slate-100 dark:border-slate-700">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold">{c.title}</p>
                          <p className="text-xs text-slate-500 mt-1">
                            {c.start_date ?? '—'} → {c.end_date ?? '—'}
                          </p>
                        </div>
                        <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-primary/10 text-primary">
                          {c.status}
                        </span>
                      </div>
                      {c.notes && <p className="text-sm text-slate-600 dark:text-slate-400 mt-3">{c.notes}</p>}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Tickets */}
            <section className="bg-white dark:bg-slate-800 rounded-2xl p-6 border border-slate-200 dark:border-slate-700 shadow-sm">
              <h2 className="text-xl font-bold mb-4">{t('portal.tickets')}</h2>

              <form onSubmit={createTicket} className="mb-6 space-y-3">
                <div>
                  <label className="block text-sm font-semibold mb-1.5">{t('portal.subject')}</label>
                  <input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1.5">{t('portal.message')}</label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                    rows={4}
                    className="w-full px-4 py-3 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-none"
                  />
                </div>
                <button
                  type="submit"
                  disabled={creating}
                  className="w-full px-6 py-3 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
                >
                  {creating ? t('portal.sending') : t('portal.submit')}
                </button>
              </form>

              {tickets.length === 0 ? (
                <p className="text-slate-600 dark:text-slate-400 text-sm">{t('portal.noTickets')}</p>
              ) : (
                <ul className="space-y-3">
                  {tickets.map((tk) => (
                    <li key={tk.id} className="p-4 rounded-xl bg-slate-50 dark:bg-slate-900/40 border border-slate-100 dark:border-slate-700">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <Link to={`/portal/tickets/${tk.id}`} className="font-semibold hover:text-primary transition-colors">
                            #{tk.id} — {tk.subject}
                          </Link>
                          <p className="text-xs text-slate-500 mt-1">{new Date(tk.updated_at).toLocaleString()}</p>
                        </div>
                        <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200">
                          {tk.status}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  )
}

